/**
 * DRR-092: Legacy Archive Browser Page
 *
 * Tenant-facing page for browsing raw legacy files from pre-FERP systems.
 * Files served via CloudFront signed URLs (storage opacity — Wasabi hidden).
 *
 * Features:
 * - File table with filename, size, content type, scan status badge
 * - Scan-gated access (INFECTED/UNSCANNED files show metadata only)
 * - In-browser viewer drawer (PDF, images, text)
 * - Download warning dialog with backend-generated warning text (DRR-055)
 * - Cursor-based pagination
 *
 * FERP Constraints:
 * - Mantine v8.x only
 * - Dumb frontend: all warning text from backend
 * - Tenant context via Apollo link headers (no explicit tenantId needed)
 * - No hardcoded field definitions (data from GraphQL)
 */

import { useState, useCallback } from 'react';
import {
  Table,
  Group,
  Text,
  ActionIcon,
  Tooltip,
  Drawer,
  Stack,
  Title,
  Alert,
  Center,
  Loader,
  Button,
  Paper,
} from '@mantine/core';
import {
  IconEye,
  IconDownload,
  IconChevronRight,
  IconArchive,
} from '@tabler/icons-react';
import { ScanStatusBadge } from '@/components/legacy-archive/ScanStatusBadge';
import { DownloadWarningDialog } from '@/components/legacy-archive/DownloadWarningDialog';
import { LegacyFileViewer } from '@/components/legacy-archive/LegacyFileViewer';
import { useLegacyArchiveFilesQuery, WarningLevel } from '@/generated/graphql';
import type { LegacyArchiveFilesQuery } from '@/generated/graphql';

/** Single file element from the LegacyArchiveFiles query (fully narrowed types). */
type LegacyFile = LegacyArchiveFilesQuery['legacyArchiveFiles'][number];

const PAGE_SIZE = 50;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function LegacyArchivePage() {
  const [viewerFile, setViewerFile] = useState<LegacyFile | null>(null);
  const [downloadFile, setDownloadFile] = useState<LegacyFile | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const { data, loading, error } = useLegacyArchiveFilesQuery({
    variables: { cursor, limit: PAGE_SIZE },
    fetchPolicy: 'network-only',
  });

  const files = data?.legacyArchiveFiles ?? [];

  const handleView = useCallback((file: LegacyFile) => {
    if (file.canView) {
      setViewerFile(file);
    }
  }, []);

  const handleDownloadClick = useCallback((file: LegacyFile) => {
    if (!file.canDownload) return;

    // If warning needed, show dialog first
    if (file.warningLevel !== WarningLevel.NONE) {
      setDownloadFile(file);
    } else {
      // Direct download (no warning for NONE level)
      if (file.downloadUrl) {
        window.open(file.downloadUrl, '_blank');
      }
    }
  }, []);

  const handleDownloadConfirm = useCallback(() => {
    if (downloadFile?.downloadUrl) {
      window.open(downloadFile.downloadUrl, '_blank');
    }
    setDownloadFile(null);
  }, [downloadFile]);

  if (error) {
    return (
      <Alert color="red" title="Error loading legacy archive">
        {error.message}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="sm">
          <IconArchive size={24} />
          <Title order={3}>Legacy Archive</Title>
        </Group>
        <Text size="sm" c="dimmed">
          Files from your previous system, scanned and secured by FERP
        </Text>
      </Group>

      {loading ? (
        <Center p="xl">
          <Loader />
        </Center>
      ) : files.length === 0 ? (
        <Alert color="blue" title="No Legacy Files">
          No legacy archive files found. Files will appear here after
          onboarding data is processed through the ETL pipeline.
        </Alert>
      ) : (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>File Name</Table.Th>
                <Table.Th>Size</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Scan Status</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {files.map((file) => (
                <Table.Tr key={file.fileId}>
                  <Table.Td>
                    <Text size="sm" fw={500} truncate="end" maw={300}>
                      {file.fileName}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {formatFileSize(file.fileSize)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {file.contentType}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <ScanStatusBadge
                      status={file.scanStatus}
                      scanDate={file.scanDate}
                      clamavVersion={file.clamavVersion}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Tooltip label={file.canView ? 'View' : 'View blocked'}>
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          disabled={!file.canView}
                          onClick={() => handleView(file)}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip
                        label={file.canDownload ? 'Download' : 'Download blocked'}
                      >
                        <ActionIcon
                          variant="subtle"
                          color="green"
                          disabled={!file.canDownload}
                          onClick={() => handleDownloadClick(file)}
                        >
                          <IconDownload size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          {/* Pagination */}
          {files.length >= PAGE_SIZE && (
            <Group justify="center" p="md">
              <Button
                variant="subtle"
                rightSection={<IconChevronRight size={16} />}
                onClick={() => {
                  const lastFile = files[files.length - 1];
                  if (lastFile) setCursor(lastFile.fileId);
                }}
              >
                Load More
              </Button>
            </Group>
          )}
        </Paper>
      )}

      {/* File Viewer Drawer */}
      <Drawer
        opened={viewerFile !== null}
        onClose={() => setViewerFile(null)}
        title={viewerFile?.fileName ?? 'File Viewer'}
        position="right"
        size="xl"
      >
        {viewerFile && (
          <LegacyFileViewer
            viewUrl={viewerFile.viewUrl ?? null}
            fileName={viewerFile.fileName}
            contentType={viewerFile.contentType}
            scanStatus={viewerFile.scanStatus}
            canView={viewerFile.canView}
          />
        )}
      </Drawer>

      {/* Download Warning Dialog */}
      {downloadFile && (
        <DownloadWarningDialog
          opened={downloadFile !== null}
          onClose={() => setDownloadFile(null)}
          onConfirm={handleDownloadConfirm}
          fileName={downloadFile.fileName}
          fileSize={downloadFile.fileSize}
          scanStatus={downloadFile.scanStatus}
          scanDate={downloadFile.scanDate}
          warningLevel={downloadFile.warningLevel}
          warningMessage={downloadFile.warningMessage ?? ''}
        />
      )}
    </Stack>
  );
}
